import React from 'react';
import {
  COUNTRY_CODES, guessCountryIso, parseTypedPhone, splitE164,
} from '../../utils/countryCodes';
import { phoneError } from '../../utils/phone';

// A curated country list must have an explicit path for everyone outside it.
// This is deliberately not an ISO code: it says the number itself, including
// its leading "+", supplies the country code.
const OTHER_COUNTRY_OPTION = '__other__';

/**
 * Country picker + subscriber number, emitting one E.164 string.
 *
 * Every phone the platform stores is E.164 WITH the leading "+" — that is what
 * utils/phone.js validates and what billing_currency.phone_error enforces on
 * the server. The boundaries that need it without the "+" strip it themselves
 * (WATI push, webhook matching), so nothing upstream should ever be asked to
 * type a bare number.
 *
 * Before this component, five separate inputs asked people to type "+91XXXXXXXXXX"
 * into free text. Two things went wrong there and neither is a typo:
 *
 *   - Leaving the code off entirely. On a WhatsApp alert number that means the
 *     message is undeliverable; on the business mobile it decides the billing
 *     currency, so it is a pricing error.
 *   - The "+91" placeholder itself, which quietly nudged every non-Indian
 *     customer toward an Indian country code.
 *
 * The value in and out is always the full E.164 string, so callers store and
 * submit exactly what they did before.
 */
const PhoneField = ({
  id,
  value,
  onChange,
  disabled = false,
  required = false,
  autoComplete = 'tel-national',
  className = '',
  selectClassName = '',
  inputClassName = '',
  placeholder = '9876543210',
  // Country for an empty field, e.g. the country of the numbers already
  // saved on the page. Wins over the time-zone guess, which follows wherever
  // the browser happens to be.
  defaultCountryIso = '',
  // Links the number box to an error shown under the field.
  describedBy,
  invalid = false,
}) => {
  const parsed = splitE164(value);
  const [otherSelected, setOtherSelected] = React.useState(false);
  const [selectedIso, setSelectedIso] = React.useState('');
  // Text that is not a phone number as typed: letters or an extension
  // ("ext 2", "#2"), or a "+" that is not at the start. It is kept exactly as
  // entered, shown in the box and passed up as the value, so the caller's
  // validation refuses it with a message. Cleaning it would merge an
  // extension's digits into somebody else's number; ignoring it would quietly
  // keep the previous recipient.
  const [rawText, setRawText] = React.useState(null);
  const [rawIso, setRawIso] = React.useState('');
  // The parent cleared or replaced the value (a reset after submit, a reload):
  // stop showing the old invalid text.
  React.useEffect(() => {
    if (rawText !== null && String(value || '') !== rawText.trim()) setRawText(null);
  }, [value, rawText]);
  const selectedCountry = COUNTRY_CODES.find((country) => country.iso === selectedIso);
  // E.164 stores a calling code, not a country. The US and Canada both store
  // as +1, so re-parsing a number after someone explicitly chooses the US
  // previously selected Canada's earlier list entry and made the picker jump.
  // Preserve the explicit choice while it still shares the stored calling
  // code; a number from a different code always wins and resets the display.
  const selectedIsoMatchesNumber = Boolean(
    selectedCountry && parsed.dial && selectedCountry.dial === parsed.dial,
  );
  // An empty field starts on the timezone guess; a field with a number in it
  // shows that number's own country. Never guess over a real value.
  const iso = (selectedIsoMatchesNumber ? selectedIso : parsed.iso)
    || (value ? '' : defaultCountryIso || guessCountryIso() || '');
  // otherSelected is component state and `value` is a prop, so the two can
  // disagree. NotificationSettingsPage renders these in a list keyed by index,
  // so deleting a row shifts every later value into a DIFFERENT component
  // instance -- one that may still be holding otherSelected from the row that
  // was removed. Ignoring the flag whenever the current value parses to a known
  // country makes that self-correct instead of showing "Other" over a perfectly
  // ordinary +91 number.
  const isOther = parsed.unknownCode || (otherSelected && !parsed.iso);

  const emit = (nextIso, nextDigits) => {
    const c = COUNTRY_CODES.find((x) => x.iso === nextIso) || null;
    if (!c) {
      onChange(nextDigits);
      return;
    }
    // A national trunk "0" is never part of the international number
    // ("098765 43210" is +91 98765 43210), except in Italy where it is. Left
    // in, it produced "+9109876543210": valid E.164, nobody's phone. The chat
    // widget and the website apply the same rule.
    // An empty number still carries the dial code: that is how a country
    // chosen before typing is remembered.
    // One trunk zero only: a leading "00" is the international prefix, and
    // parseTypedPhone has already read that as "+".
    const national = c.iso === 'IT' ? nextDigits : nextDigits.replace(/^0/, '');
    onChange(`${c.dial}${national}`);
  };

  /**
   * Everything typed, pasted or autofilled into the number box goes through
   * here. parseTypedPhone decides whether the text carries its own country
   * code, and its rule is deliberately narrow: only a leading "+".
   *
   * Two failures sit either side of that line. Prefixing the selected dial code
   * onto a pasted "+919876543210" produced "+91919876543210" — fourteen digits
   * that satisfy E.164 and belong to nobody. But inferring a country from bare
   * digits instead is just as wrong in the other direction: a German mobile
   * typed as 15123456789 would be read as +1 5123456789, jumping the picker to
   * the US and storing a North American number.
   *
   * A stored value gets the looser rule (splitE164) because we know it was
   * persisted country-coded. Typed text is not that.
   */
  const handleInput = (raw) => {
    const text = String(raw ?? '');
    if (/[^\d\s\-()./+]/.test(text) || text.trim().lastIndexOf('+') > 0) {
      setRawIso(rawText === null ? iso : rawIso);
      setRawText(text);
      onChange(text.trim());
      return;
    }
    // Leaving invalid text: the country on screen was rawIso, and the stored
    // value (the invalid text) carries none, so carry it over explicitly.
    const leavingRaw = rawText !== null;
    const shownIso = leavingRaw ? rawIso : iso;
    if (leavingRaw) {
      setRawText(null);
      setSelectedIso(rawIso);
      setOtherSelected(!rawIso);
    }
    const parsed = parseTypedPhone(raw);
    // Cleared box means cleared value. Emitting the bare dial code instead
    // would leave "+91" sitting in state and fail validation as "too short",
    // which is a confusing thing to say to someone who just deleted a number.
    if (!parsed.digits) {
      onChange('');
      return;
    }
    if (parsed.unknownCode) {
      // Explicit "+" with a dialling code the curated list does not carry.
      // It is still a complete number — keep it verbatim rather than stripping
      // the "+" and prefixing whatever the picker happens to show, which turned
      // a pasted "+7..." into "+917...". Show the explicit Other option and
      // let phoneError judge it like any other E.164 value.
      setOtherSelected(true);
      onChange(`+${parsed.digits}`);
      return;
    }
    if (parsed.iso) {
      // Carried its own code — adopt it, and move the picker to match so the
      // person can see which country the number was read as. If their explicit
      // choice shares that code (US/Canada +1), keep that choice: E.164 itself
      // cannot distinguish the two countries.
      setOtherSelected(false);
      setSelectedIso(
        selectedCountry?.dial === parsed.dial ? selectedIso : parsed.iso,
      );
      onChange(`${parsed.dial}${parsed.digits}`);
      return;
    }
    if (leavingRaw ? !rawIso : isOther) {
      // Other means a full +number is required. Never let the timezone guess
      // silently turn bare digits into a number for a different country.
      onChange(parsed.digits);
      return;
    }
    emit(shownIso, parsed.digits);
  };

  const handleCountryChange = (nextIso) => {
    if (rawText !== null) {
      // The box still holds text that is not a number; changing the country
      // cannot make it one. Keep it, and its error, until it is corrected.
      setRawIso(nextIso === OTHER_COUNTRY_OPTION ? '' : nextIso);
      return;
    }
    if (nextIso === OTHER_COUNTRY_OPTION) {
      // A selected country prefixes all subscriber digits. Clear rather than
      // re-home an existing number when the person explicitly chooses Other.
      setOtherSelected(true);
      setSelectedIso('');
      onChange('');
      return;
    }
    setOtherSelected(false);
    setSelectedIso(nextIso);
    // An unlisted full number cannot safely be repurposed as subscriber
    // digits for a selected country, so changing away from Other starts clean.
    if (isOther) {
      onChange('');
      return;
    }
    emit(nextIso, parsed.digits);
  };

  return (
    <div className={`flex gap-2 ${className}`}>
      <select
        id={id ? `${id}-country` : undefined}
        aria-label="Country code"
        disabled={disabled}
        /* Other means the full number carries its own dialling code. The input
           remains required, but the picker itself cannot add anything useful;
           phoneFieldError and the server's E.164 check judge that number. */
        required={required && !isOther}
        value={rawText !== null ? rawIso : (isOther ? OTHER_COUNTRY_OPTION : iso)}
        onChange={(e) => handleCountryChange(e.target.value)}
        className={selectClassName || 'w-36 shrink-0 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-2 text-sm text-gray-900 dark:text-white'}
      >
        {/* Blank when the timezone guess was not confident. Making someone
            choose beats defaulting them into the wrong country. */}
        <option value="" disabled>Country</option>
        {COUNTRY_CODES.map((c) => (
          <option key={c.iso} value={c.iso}>{c.name} {c.dial}</option>
        ))}
        <option value={OTHER_COUNTRY_OPTION}>Other — enter full +country code number</option>
      </select>
      <input
        id={id}
        type="tel"
        inputMode="tel"
        disabled={disabled}
        required={required}
        autoComplete={autoComplete}
        aria-describedby={describedBy}
        aria-invalid={invalid || undefined}
        /* Generous on purpose. maxLength applies to PASTED text, so any cap
           near the digit count silently truncates a formatted number:
           "+1 (212) 555-0123" is seventeen characters and would have been cut
           to "+1212555012" — eleven digits that still pass E.164 validation
           and reach the wrong person. Length is judged after normalisation, by
           phoneError, which can say "too long" instead of quietly mangling. */
        maxLength={32}
        /* An unlisted country code has no picker entry to carry the "+", so
           it is shown in the box itself. Without that, the next keystroke would
           re-read the value as subscriber digits and lose it. */
        value={rawText !== null ? rawText : (isOther ? String(value || '') : parsed.digits)}
        onChange={(e) => handleInput(e.target.value)}
        placeholder={isOther ? '+7 999 123 4567' : placeholder}
        className={inputClassName || 'min-w-0 flex-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white'}
      />
    </div>
  );
};

/**
 * The reason a composed number is unusable, or null when it is fine.
 * Re-exported so callers gate their save button on the same rule the field
 * itself applies, rather than importing two things that could disagree.
 */
export const phoneFieldError = (value) => {
  // Text that is not a phone number at all gets its own reason first
  // ("no letters or extension", "+ at the start"), not "choose a country".
  const compact = String(value || '').replace(/[\s\-()./]/g, '');
  if (/[^\d+]/.test(compact) || compact.lastIndexOf('+') > 0) return phoneError(value);
  const { iso } = splitE164(value);
  if (!iso && !String(value || '').startsWith('+')) {
    // Names BOTH ways out, because this fires in two situations and a message
    // covering only one of them is a dead end. Someone who has explicitly
    // chosen "Other" and typed a local number was being told to "Choose a
    // country" -- which they just did, so the dropdown offers them nothing.
    return 'Choose your country, or type the full number including its + country code';
  }
  return phoneError(value);
};

export default PhoneField;
